import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { PlaceApi, placesApi } from "api";
import { usePlaceTags, usePlaces } from "api/hooks";
import { routes } from "consts";
import { useDeleteResourceActions } from "utils/store";
import { AdminResourceDeleteModal, AdminSearchInput, PageMain } from "components/molecules";
import { PaziaButton, PaziaLoader } from "components/atoms";
import {
  TanTable,
  useFilters,
  usePagination,
  useSorting,
  TanTableColumn,
} from "components/molecules";
import { PaziaTableFilter, PaziaTableFilters } from "components/molecules/SearchableAdminTable/PaziaTableFilters";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";

export const PlacesPage = () => {
  const { setDeleteResource } = useDeleteResourceActions();
  const { searchPhrase, filter, setSearchPhrase, changeFilter, getInitFilterValues } = useFilters("places-filter");
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting();

  const navigate = useNavigate();

  const { isLoading, isFetching, places, total, invalidate } = usePlaces(
    skip,
    limit,
    searchPhrase,
    filter,
    fieldWithOrder,
  );

  const { isLoading: loadingTags, placeTags } = usePlaceTags(0, 1_000_000);

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase, filter]);

  const filters = placeTags.map((x) => ({ id: x.id.toString(), name: x.name }));

  const columns = React.useMemo<TanTableColumn<PlaceApi>[]>(() => cols, []);

  if (loadingTags) return <PaziaLoader position="center" />;

  return (
    <PageMain>
      <PageRichHeader header="Miejsca">
        <div className="flex grow justify-between">
          <div>
            <PaziaButton type="primary" title="Dodaj +" onClick={() => navigate(routes.PLACES_ADD)} />
          </div>
          <div>
            <PaziaButton type="tertiary" title="Edytuj tagi" onClick={() => navigate(routes.PLACE_TAGS)} />
          </div>
        </div>
      </PageRichHeader>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <AdminSearchInput onSearch={(searchPhrase: string) => setSearchPhrase(searchPhrase)} className="self-end" />
      </div>
      <PaziaTableFilters
        filters={filters}
        onFiltersChange={(fs) => changeFilter("tags", mapTagsFilter(fs))}
        defaultFilters={getInitFilterValues("tags")}
      />
      <TanTable
        data={places}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        onDelete={(x) => setDeleteResource(x.id)}
        pagination={pagination}
        sorting={sorting}
        onRowClick={(x) => navigate(routes.PLACES_EDIT(x.id))}
      />
      <AdminResourceDeleteModal
        title="Usuń miejsce"
        description="Jesteś pewien że chcesz usunąć miejsce?"
        onSuccessDelete={() => invalidate()}
        deleteAction={placesApi.delete}
      />
    </PageMain>
  );
};

const mapTagsFilter = (filters: PaziaTableFilter[]) =>
  filters.length > 0 ? `tags in (${filters.map((x) => `'${x.id}'`).join(",")})` : "";

const cols: TanTableColumn<PlaceApi>[] = [
  {
    id: "id",
    hideOnMobile: true,
    accessorKey: "id",
    header: "ID",
    enableColumnFilter: false,
    size: 10,
    cell: (info) => info.getValue(),
  },
  {
    id: "name",
    accessorKey: "name",
    header: "Nazwa",
    size: 500,
    enableColumnFilter: false,
  },
  {
    id: "address",
    hideOnMobile: true,
    accessorKey: "address",
    header: "Adres",
    size: 500,
    enableColumnFilter: false,
  },
  {
    id: "city",
    accessorKey: "city",
    header: "Miasto",
    size: 500,
    enableColumnFilter: false,
  },
  {
    id: "createdDate",
    hideOnMobile: true,
    accessorKey: "createdDate",
    header: "Utworzono",
    cell: (info) => info.getValue<Date>().toLocaleString(),
    size: 250,
    enableColumnFilter: false,
  },
];
